import type { InternalAxiosRequestConfig } from "axios";

/**
 * Per-session API token issued by the user-service at login time.
 * Kept in module memory as the primary store; sessionStorage is used as a
 * fallback so the token survives page refreshes within the same browser tab.
 * sessionStorage is cleared when the tab closes, matching the session lifetime.
 * The token is worthless without the accompanying JWT, so XSS exposure risk is
 * no worse than the JWT cookies (which are already readable by JS).
 */
const SESSION_STORAGE_KEY = "_advance_sat";
let _sessionApiToken: string | null = null;

/** Called right after a successful login with the token from the login response. */
export function setSessionApiToken(token: string): void {
  _sessionApiToken = token || null;
  if (_sessionApiToken) {
    try { sessionStorage.setItem(SESSION_STORAGE_KEY, _sessionApiToken); } catch { /* quota / private mode */ }
  }
}

/** Called on logout to wipe both the in-memory and stored token. */
export function clearSessionApiToken(): void {
  _sessionApiToken = null;
  try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* ignore */ }
}

/** Restore the token from sessionStorage after a page refresh. */
function resolveToken(): string | null {
  if (_sessionApiToken) return _sessionApiToken;
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) { _sessionApiToken = stored; }
  } catch { /* ignore */ }
  return _sessionApiToken;
}

/**
 * Attach per-session HMAC headers to an outbound axios request.
 *
 * x-api-token  — HMAC derived from JWT claims, computed server-side at login.
 * x-request-ts — current Unix timestamp in milliseconds for replay prevention.
 */
export function applySessionApiTokenToAxiosConfig(
  config: InternalAxiosRequestConfig
): void {
  const token = resolveToken();
  if (!token) return;

  config.headers = config.headers ?? {};
  const h = config.headers as Record<string, string>;
  h["x-api-token"]  = token;
  h["x-request-ts"] = String(Date.now());
}
